/**
 * 设备通信协议处理模块
 * 包含设备 HID 管理、命令发送、响应处理
 * 对齐旧版 js/server-1.js 的通信逻辑
 * 
 * 导入类型和协议处理函数
 */
import type { DeviceInfo, DeviceDetail, DeviceStatus, ConnectionType } from './types'
import { formatDeviceStatusForLog, formatDeviceDetailForLog } from './status-log'
import {
  buildCommand,
  buildGetStatusCommand,
  buildGetDeviceDetailCommand,
  parseHeadsetResponse,
  parseDeviceDetailResponse,
  bytesToHexString,
} from './protocol'

/**
 * HID 事件类型映射
 * 定义所有可能的事件及其数据类型
 */
// 事件类型
export interface HIDDeviceEventMap {
  'device-inserted': { connectionType: ConnectionType }
  'device-connected': DeviceDetail
  'device-removed': { connectionType: ConnectionType }
  'device-disconnected': void
  'status-updated': DeviceStatus
  'data-received': Uint8Array
  'error': Error
  'connection-stage': {
    stage:
      | 'idle'
      | 'authorized_found'
      | 'requesting_authorization'
      | 'opening_device'
      | 'waiting_for_pairing'
      | 'connected'
      | 'disconnected'
      | 'upgrade_mode'
    message?: string
    connectionType?: ConnectionType
  }
}

type EventCallback<T extends keyof HIDDeviceEventMap> = (data: HIDDeviceEventMap[T]) => void

type StoredDeviceState = {
  isConnected?: boolean
  isDeviceConnected?: boolean
  connectionType?: ConnectionType
  deviceInfo?: {
    productId: number
    vendorId: number
    productName: string
  }
}

const STORAGE_KEY = 'deviceConnectionState'

function loadStoredState(): StoredDeviceState {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}') as StoredDeviceState
  } catch {
    return {}
  }
}

function saveStoredState(state: StoredDeviceState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {
    // 忽略
  }
}

function clearStoredState(): void {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    // 忽略
  }
}

/**
 * 控制台协议日志统一格式。
 * - 下发：蓝色 [发送命令→设备]
 * - 上报：绿色 [设备上报]
 * 保持与旧版 server-1.js 调试输出一致，便于直接区分数据方向。
 */
function formatProtocolTimestamp(date = new Date()): string {
  const pad2 = (value: number) => String(value).padStart(2, '0')
  const pad3 = (value: number) => String(value).padStart(3, '0')
  return `${date.getFullYear()}/${pad2(date.getMonth() + 1)}/${pad2(date.getDate())} `
    + `${pad2(date.getHours())}:${pad2(date.getMinutes())}:${pad2(date.getSeconds())}.${pad3(date.getMilliseconds())}`
}

function getFrameCommand(bytes: Uint8Array): number | null {
  if (bytes.length < 4 || bytes[0] !== 0xAA || bytes[1] !== 0x53) return null
  return bytes[3]
}

function getCommandDescription(bytes: Uint8Array): string {
  const command = getFrameCommand(bytes)
  if (command == null) return '未知数据'

  if (command === 0x52) {
    const sub = bytes[4]
    const eqSubNames: Record<number, string> = {
      0x02: 'EQ单点数据',
      0x13: 'EQ登录状态',
      0x14: 'EQ/吃鸡开关与标签',
      0x15: 'EQ数据1',
      0x16: 'EQ数据2',
      0x17: 'EQ数据3',
      0x18: 'EQ数据4',
    }
    const subName = eqSubNames[sub] || `EQ子命令(0x${sub.toString(16).padStart(2, '0').toUpperCase()})`
    return `${subName} (0x52/0x${sub.toString(16).padStart(2, '0').toUpperCase()})`
  }

  if (command === 0x5B) {
    const sub = bytes[4]
    const subNames: Record<number, string> = {
      0x00: '音频平衡调节',
      0x01: '音频模式切换',
      0x02: '耳返音量',
      0x03: '耳返开关',
      0x04: '蓝牙音频分享',
      0x05: '多方通话桥接',
    }
    const subName = subNames[sub] || `音频子命令(0x${sub.toString(16).padStart(2, '0').toUpperCase()})`
    return `${subName} (0x5B/0x${sub.toString(16).padStart(2, '0').toUpperCase()})`
  }

  const names: Record<number, string> = {
    0x51: 'AI降噪',
    0x54: '喇叭静音',
    0x55: '麦克风静音',
    0x56: '喇叭音量',
    0x57: '麦克风音量',
    0x58: '抗干扰',
    0x60: 'RGB灯效',
    0x61: '魔幻音效',
    0x70: '电源控制',
    0x72: '场景应用（0x20 字节快照）',
    0x80: '获取设备状态',
    0x82: '获取设备详情',
  }
  const hex = `0x${command.toString(16).padStart(2, '0').toUpperCase()}`
  return `${names[command] || '未知命令'} (${hex})`
}

function logOutgoingFrame(bytes: Uint8Array, waitForResponse: boolean): void {
  const hex = bytesToHexString(bytes)
  console.log(
    '%c[发送命令→设备]%c %s | %s | %s | 原始: %s',
    'background:#2F80ED;color:#FFF;padding:2px 7px;border-radius:3px;font-weight:600;',
    'color:#2F80ED;',
    formatProtocolTimestamp(),
    getCommandDescription(bytes),
    waitForResponse ? '等待回复' : '无需回复',
    hex,
  )
}

export function isWebHIDSupported(): boolean {
  return typeof navigator !== 'undefined' && 'hid' in navigator && !!navigator.hid
}

export function getWebHIDSupportError(): string | null {
  if (typeof navigator === 'undefined') return '浏览器环境不支持'
  if (!('hid' in navigator)) return '当前浏览器不支持 WebHID API，请使用 Chrome/Edge 浏览器'
  if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
    return 'WebHID 需要 HTTPS 安全连接'
  }
  return null
}

/**
 * WebHID 设备管理器类
 * 
 * 实现功能:
 * - 记住上次授权的设备 (localStorage)
 * - 启动时自动连接授权设备，无需权限提示
 * - 根据状态标志位等待真正的配对成功
 * - 设备插拔时触发 UI 阶段事件和状态清理
 */
export class HIDDeviceManager {
  private device: HIDDevice | null = null
  private listeners: Map<keyof HIDDeviceEventMap, Set<EventCallback<any>>> = new Map()
  private inputReportHandler: ((e: HIDInputReportEvent) => void) | null = null
  private externalDisconnectHandler: ((e: HIDConnectionEvent) => void) | null = null
  private externalConnectHandler: ((e: HIDConnectionEvent) => void) | null = null

  private isAutoConnecting = false
  private isDeviceConnected = false // 对应旧项目 pc_isConnected
  private lastConnectedDeviceKey: string | null = null // 记录最近插入的设备

  // 单监听器架构：通过状态机匹配命令和响应
  private commandResolver: ((value: Uint8Array) => void) | null = null
  private commandRejecter: ((reason: Error) => void) | null = null
  private commandTimeout: ReturnType<typeof setTimeout> | null = null
  private isWaitingForResponse = false

  // 旧项目使用的厂商/产品 ID
  private readonly DEVICE_FILTERS = [
    { vendorId: 0x1235, productId: 0x0043 },
    { vendorId: 0x2235, productId: 0x0059 },
    { vendorId: 0x1235, productId: 0x0057 },
    { vendorId: 0x1235, productId: 0x0067 },
    { vendorId: 0x1235, productId: 0x0059 },
    // DM05 USB MIC: USB\VID_1235&PID_EF00 (十进制 4661/61184)
    { vendorId: 4661, productId: 61184 },
    // 兼容旧硬件使用的 VID 13586
    { vendorId: 13586, productId: 67 },
    { vendorId: 13586, productId: 61184 },
  ]

  private emit<T extends keyof HIDDeviceEventMap>(event: T, data: HIDDeviceEventMap[T]): void {
    const callbacks = this.listeners.get(event)
    if (callbacks) {
      callbacks.forEach(cb => {
        try {
          cb(data)
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('事件处理器错误:', error)
        }
      })
    }
  }

  on<T extends keyof HIDDeviceEventMap>(event: T, callback: EventCallback<T>): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set())
    }
    this.listeners.get(event)!.add(callback as EventCallback<any>)

    return () => {
      this.listeners.get(event)?.delete(callback as EventCallback<any>)
    }
  }

  getConnectionType(): ConnectionType {
    return this.getConnectionTypeForDevice(this.device)
  }

  private getConnectionTypeForDevice(device: HIDDevice | null): ConnectionType {
    if (!device) return null
    // 旧项目映射：
    // 2.4G：PID 67
    // USB：PID 89
    if (device.productId === 67 || device.productId === 61184) return '2.4G'
    if (device.productId === 89) return 'USB'

    // 兼容现有筛选列表中的十六进制 PID
    if (device.productId === 0x0043 || device.productId === 0x0057) return '2.4G'
    if (device.productId === 0x0059 || device.productId === 0x0067) return 'USB'

    return null
  }

  private checkDeviceConnectionStatus(status: DeviceStatus): boolean {
    const connectionType = this.getConnectionType()

    if (connectionType === '2.4G') {
      return status.headsetConnected === 1
    }

    if (connectionType === 'USB') {
      return status.linkStatus === 1
    }

    return false
  }

  private hasReport(device: HIDDevice, reportId: number): boolean {
    return device.collections.some(col => {
      const inputReports = col.inputReports || []
      const outputReports = col.outputReports || []
      return inputReports.some(r => r.reportId === reportId) || outputReports.some(r => r.reportId === reportId)
    })
  }

  private hasNormalModeReport49(device: HIDDevice): boolean {
    return device.collections.some(col => {
      const inputReports = col.inputReports || []
      const outputReports = col.outputReports || []
      const hasInput49 = inputReports.some(r => r.reportId === 49)
      const hasOutput49 = outputReports.some(r => r.reportId === 49)
      return hasInput49 && hasOutput49
    })
  }

  private hasUpgradeModeReport0(device: HIDDevice): boolean {
    return device.collections.some(col => {
      const inputReports = col.inputReports || []
      const outputReports = col.outputReports || []
      const hasInput0 = inputReports.some(r => r.reportId === 0)
      const hasOutput0 = outputReports.some(r => r.reportId === 0)
      return hasInput0 && hasOutput0
    })
  }

  /**
   * 获取已授权的设备列表
   * 只返回支持报告 ID 49 的设备（正常模式）
   * 
   * @returns 已授权的 HID 设备数组
   */
  async getAuthorizedDevices(): Promise<HIDDevice[]> {
    if (!navigator.hid) return []

    const devices = await navigator.hid.getDevices()
    // 旧项目的“业务匹配”条件为 reportId=49
    return devices.filter(d => this.hasReport(d, 49))
  }

  async hasAuthorizedDevices(): Promise<boolean> {
    const devices = await this.getAuthorizedDevices()
    return devices.length > 0
  }

  /**
   * 初始化 HID 管理器
   * 在应用启动时调用，设置全局监听器并尝试自动连接
   * 
   * @returns 是否成功初始化（true=初始化成功且已尝试自动连接）
   */
  async init(): Promise<boolean> {
    if (!isWebHIDSupported()) {
      const msg = getWebHIDSupportError() || 'WebHID API not supported'
      this.emit('error', new Error(msg))
      return false
    }

    this.setupGlobalHIDListeners()
    return this.autoConnect()
  }

  /**
   * 自动连接（无权限提示）
   * 连接到已授权的设备，使用 navigator.hid.getDevices() 获取
   * 
   * @returns 连接是否成功
   */
  async autoConnect(): Promise<boolean> {
    if (this.isAutoConnecting || this.device) return false
    if (!navigator.hid) {
      this.emit('error', new Error('WebHID API not supported'))
      return false
    }

    this.isAutoConnecting = true

    try {
      const allDevices = await navigator.hid.getDevices()

      const hasUpgrade = allDevices.some(d => this.hasUpgradeModeReport0(d))
      const hasNormal = allDevices.some(d => this.hasNormalModeReport49(d))
      if (hasUpgrade && !hasNormal) {
        this.emit('connection-stage', { stage: 'upgrade_mode', message: '检测到升级模式设备' })
        this.isAutoConnecting = false
        return false
      }

      const devices = await this.getAuthorizedDevices()
      if (devices.length === 0) {
        this.isAutoConnecting = false
        return false
      }

      this.emit('connection-stage', { stage: 'authorized_found' })

      // 优先取最新插入的设备（lastConnectedDeviceKey），其次取已保存的设备，最后取第一个
      let target: HIDDevice = devices[0]
      if (this.lastConnectedDeviceKey) {
        const [vid, pid] = this.lastConnectedDeviceKey.split(':').map(Number)
        target = devices.find(d => d.vendorId === vid && d.productId === pid) || target
      } else {
        const saved = loadStoredState()
        if (saved.deviceInfo) {
          target = devices.find(d => d.productId === saved.deviceInfo!.productId && d.vendorId === saved.deviceInfo!.vendorId) || target
        }
      }

      const ok = await this.connectToDevice(target)
      this.isAutoConnecting = false
      return ok
    } catch (error) {
      this.emit('error', error instanceof Error ? error : new Error(String(error)))
      this.isAutoConnecting = false
      return false
    }
  }

  /**
   * 手动连接（显示权限提示窗口）
   * 用户手动点击连接时调用，会弹出系统权限提示
   * 
   * @returns 连接是否成功
   */
  async connect(): Promise<boolean> {
    if (!isWebHIDSupported() || !navigator.hid) {
      const msg = getWebHIDSupportError() || 'WebHID API not supported'
      this.emit('error', new Error(msg))
      return false
    }

    this.emit('connection-stage', { stage: 'requesting_authorization' })

    try {
      const devices = await navigator.hid.requestDevice({ filters: this.DEVICE_FILTERS })
      if (devices.length === 0) return false

      // 优先使用正常模式 reportId=49 的设备；如果只有升级模式设备则进入对应阶段
      let normal: HIDDevice | null = null
      let upgrade: HIDDevice | null = null

      for (const dev of devices) {
        if (this.hasNormalModeReport49(dev)) normal = dev
        if (this.hasUpgradeModeReport0(dev)) upgrade = dev
      }

      if (normal) {
        return await this.connectToDevice(normal)
      }

      if (upgrade) {
        this.emit('connection-stage', { stage: 'upgrade_mode', message: '设备处于升级模式' })
        return false
      }

      return false
    } catch (error) {
      this.emit('error', error instanceof Error ? error : new Error(String(error)))
      return false
    }
  }

  private async connectToDevice(targetDevice: HIDDevice): Promise<boolean> {
    console.log('[HID] 正在连接设备:', targetDevice.productName, 'VID:', targetDevice.vendorId, 'PID:', targetDevice.productId)
    this.emit('connection-stage', { stage: 'opening_device' })

    this.device = targetDevice
    this.isDeviceConnected = false

    try {
      if (!this.device.opened) {
        console.log('[HID] 正在打开设备...')
        await this.device.open()
        console.log('[HID] 设备打开成功')
      } else {
        console.log('[HID] 设备已经打开')
      }

      const connectionType = this.getConnectionType()

      // 将状态保存到 localStorage，记住已授权设备
      saveStoredState({
        isConnected: true,
        isDeviceConnected: false,
        connectionType,
        deviceInfo: {
          productId: this.device.productId,
          vendorId: this.device.vendorId,
          productName: this.device.productName,
        },
      })

      // 先挂 inputreport 监听，再发首包；否则可能错过第一帧响应
      this.setupEventListeners()

      // 这里只发阶段事件，不应该强制驱动独立等待页面
      if (connectionType === '2.4G') {
        this.emit('connection-stage', { stage: 'waiting_for_pairing', message: '等待耳机配对', connectionType })
      } else if (connectionType === 'USB') {
        this.emit('connection-stage', { stage: 'waiting_for_pairing', message: '等待USB连接', connectionType })
      }

      // 获取设备状态和详情（对应旧项目 fetchDeviceStatus）。
      // 该流程会依次发送命令并等待响应。
      this.fetchDeviceStatusAndDetail().catch(console.error)

      return true
    } catch (error) {
      clearStoredState()
      this.device = null
      this.emit('error', error instanceof Error ? error : new Error(String(error)))
      return false
    }
  }

  async disconnect(): Promise<void> {
    this.removeEventListeners()

    if (this.device && this.device.opened) {
      try {
        await this.device.close()
      } catch {
        // 忽略
      }
    }

    this.device = null
    this.isDeviceConnected = false
    clearStoredState()
    this.emit('connection-stage', { stage: 'disconnected' })
    this.emit('device-disconnected', undefined)
  }

  /**
   * 发送原始数据到设备
   * 
   * @param data - 要发送的数据（Uint8Array 或十六进制字符串）
   * @param waitForResponse - 是否等待设备响应
   * @returns 如果 waitForResponse 为 true，返回设备响应；否则返回 undefined
   */
  async sendData(data: Uint8Array | string, waitForResponse = false): Promise<Uint8Array | void> {
    if (!this.device || !this.device.opened) {
      throw new Error('Device not connected')
    }

    // 如果已有命令正在等待响应，则禁止并发发送，避免响应错配。
    if (this.isWaitingForResponse) {
      throw new Error('Another command is already waiting for response')
    }

    const dataArray =
      typeof data === 'string' ? new Uint8Array(data.split(' ').map(h => parseInt(h, 16))) : data

    logOutgoingFrame(dataArray, waitForResponse)

    if (!waitForResponse) {
      await this.device.sendReport(49, dataArray)
      return
    }

    // 必须在发送前注册响应状态。部分设备会立即返回响应，
    // 如果在 sendReport() 之后才注册监听状态，就会丢失首个 inputreport 事件并等到超时。
    console.log('[HID] 正在注册响应监听...')
    this.isWaitingForResponse = true

    let rejectResponse: ((reason: Error) => void) | null = null
    const responsePromise = new Promise<Uint8Array>((resolve, reject) => {
      rejectResponse = reject
      this.commandTimeout = setTimeout(() => {
        this.clearCommandState()
        console.log('[HID] 等待响应超时')
        reject(new Error('Device response timeout'))
      }, 5000)

      this.commandResolver = resolve
      this.commandRejecter = reject
    })

    try {
      await this.device.sendReport(49, dataArray)
    } catch (error) {
      this.clearCommandState()
      rejectResponse?.(error instanceof Error ? error : new Error(String(error)))
    }

    return responsePromise
  }

  private clearCommandState(): void {
    if (this.commandTimeout) {
      clearTimeout(this.commandTimeout)
      this.commandTimeout = null
    }
    this.commandResolver = null
    this.commandRejecter = null
    this.isWaitingForResponse = false
  }

  /**
   * 发送命令到设备并等待响应
   * 
   * @param command - 命令类型
   * @param data - 命令数据
   * @returns 设备状态对象或 null（如果解析失败）
   */
  async sendCommand(command: number, data: number[] = []): Promise<DeviceStatus | null> {
    try {
      const frame = buildCommand(command as any, data)
      const response = await this.sendData(frame, true)
      if (!response) return null

      const hexString = bytesToHexString(response)
      return parseHeadsetResponse(hexString, this.getConnectionType())
    } catch (error) {
      this.emit('error', error instanceof Error ? error : new Error(String(error)))
      return null
    }
  }

  /**
   * 获取设备状态和详情，对应旧项目 fetchDeviceStatus()。
   * 命令必须串行发送并等待响应，不能并行占用 HID 通道。
   */
  private async fetchDeviceStatusAndDetail(): Promise<void> {
    if (!this.device || !this.device.opened) {
      console.log('[HID] 无法获取状态：设备尚未打开')
      return
    }

    try {
      // 1) 先拿设备详情，确保主界面可渲染正确机型素材。详情是可选信息；
      // 某些固件不响应 0x82 时，不能阻断后续 0x80 状态确认。
      console.log('[HID] 第一步：获取设备详情...')
      try {
        const frame1 = buildGetDeviceDetailCommand()
        const response1 = await this.sendData(frame1, true)
        if (response1) {
          const hexString = bytesToHexString(response1)
          const detail = parseDeviceDetailResponse(hexString)
          if (detail) {
            console.log('[HID] 设备详情:', detail.deviceName)
            this.emit('device-connected', detail)
          }
        }
      } catch (error) {
        console.warn('[HID] 无法获取设备详情，继续查询设备状态:', error)
      }

      // 2) 再拿状态帧，用 linkStatus/headsetConnected 最终确认业务连接
      console.log('[HID] 第二步：获取设备状态...')
      const frame2 = buildGetStatusCommand()
      const response2 = await this.sendData(frame2, true)
      if (response2) {
        const hexString = bytesToHexString(response2)
        const status = parseHeadsetResponse(hexString, this.getConnectionType())
        if (status) {
          console.log('[HID] 已收到设备状态，电量:', status.batteryLevel)
          this.emit('status-updated', status)
          // 只从 false -> true 提升一次，避免短时波动造成连接态抖动
          if (!this.isDeviceConnected) {
            const ok = this.checkDeviceConnectionStatus(status)
            if (ok) {
              console.log('[HID] 已确认设备连接成功！')
              this.isDeviceConnected = true
              const saved = loadStoredState()
              saveStoredState({ ...saved, isDeviceConnected: true })
              this.emit('connection-stage', { stage: 'connected', connectionType: this.getConnectionType() })
            }
          }
        }
      }
    } catch (error) {
      console.error('[HID] 获取设备状态/详情失败:', error)
    }
  }

  /**
   * 获取设备状态
   * 请求设备当前的各项状态参数
   * 
   * @returns 设备状态对象或 null
   */
  async getDeviceStatus(): Promise<DeviceStatus | null> {
    try {
      const frame = buildGetStatusCommand()
      const response = await this.sendData(frame, true)
      if (!response) return null

      const hexString = bytesToHexString(response)
      const status = parseHeadsetResponse(hexString, this.getConnectionType())

      // 确认设备配对状态（对应旧项目 onDeviceConnectedSuccessfully）
      if (status && !this.isDeviceConnected) {
        const ok = this.checkDeviceConnectionStatus(status)
        if (ok) {
          this.isDeviceConnected = true
          const saved = loadStoredState()
          saveStoredState({ ...saved, isDeviceConnected: true })
          this.emit('connection-stage', { stage: 'connected', connectionType: this.getConnectionType() })

          const detail = await this.getDeviceDetail()
          if (detail) {
            this.emit('device-connected', detail)
          }
        }
      }

      return status
    } catch (error) {
      this.emit('error', error instanceof Error ? error : new Error(String(error)))
      return null
    }
  }

  /**
   * 获取设备详情
   * 请求设备名称、版本等详细信息
   * 
   * @returns 设备详情对象或 null
   */
  async getDeviceDetail(): Promise<DeviceDetail | null> {
    try {
      const frame = buildGetDeviceDetailCommand()
      const response = await this.sendData(frame, true)
      if (!response) return null

      const hexString = bytesToHexString(response)
      return parseDeviceDetailResponse(hexString)
    } catch (error) {
      this.emit('error', error instanceof Error ? error : new Error(String(error)))
      return null
    }
  }

  isConnected(): boolean {
    return !!this.device && this.device.opened
  }

  /** 供固件升级模块获取底层 HID 设备。 */
  getHidDevice(): HIDDevice | null {
    return this.device
  }

  getDeviceInfo(): DeviceInfo | null {
    if (!this.device) return null

    return {
      productId: this.device.productId,
      vendorId: this.device.vendorId,
      productName: this.device.productName,
      serialNumber: this.device.serialNumber,
      opened: this.device.opened,
    }
  }

  private logIncomingFrame(value: Uint8Array): DeviceStatus | null {
    const hexString = bytesToHexString(value)
    const command = getFrameCommand(value)
    const description = getCommandDescription(value)

    console.log(
      '%c[设备上报]%c %s | %s',
      'background:#27AE60;color:#FFF;padding:2px 7px;border-radius:3px;font-weight:600;',
      'color:#27AE60;',
      formatProtocolTimestamp(),
      description,
    )
    console.log('原始:', hexString)

    if (command === 0x80) {
      const status = parseHeadsetResponse(hexString, this.getConnectionType())
      if (status) {
        // 这里打印的是完整 0x80 解析结果，不再只打印吃鸡 chickenOneKey/chickenTag。
        // USB 与 2.4G 两套字段都会保留，方便直接排查任意模块。
        console.log('解析:', formatDeviceStatusForLog(status))
      } else {
        console.warn('解析: 0x80 数据解析失败')
      }
      return status
    }

    if (command === 0x82) {
      const detail = parseDeviceDetailResponse(hexString)
      if (detail) {
        console.log('解析:', { 'connectionType-当前连接方式': this.getConnectionType(), ...formatDeviceDetailForLog(detail) })
      } else {
        console.warn('解析: 0x82 数据解析失败')
      }
      return null
    }

    console.log('解析:', {
      'type-数据类型': description,
      'command-命令字': command == null ? null : `0x${command.toString(16).padStart(2, '0').toUpperCase()}`,
      'data-原始数据': bytesToHexString(value),
    })
    return null
  }

  private setupEventListeners(): void {
    if (!this.device) return

    console.log('[HID] 正在设置输入报告监听器')

    this.inputReportHandler = (e: HIDInputReportEvent) => {
      if (e.reportId !== 49) return

      const value = new Uint8Array(e.data.buffer, e.data.byteOffset, e.data.byteLength)
      const isLegacyControlFrame = value[0] === 0xAA && value[1] === 0x53

      // SBT OTA 在同一个 report ID 上使用 AA 55 报文，而主页解析器只处理 AA 53。
      // OTA 报文不得转交主页解析，否则会产生错误的包头告警，
      // 甚至可能被普通命令响应通道误消费。
      if (!isLegacyControlFrame) return

      // 所有 AA 53 上报先按统一的绿色“设备上报”格式打印。
      // 0x80 会同时输出完整解析对象，方便查看所有模块状态。
      const status = this.logIncomingFrame(value)

      // 1) 命令-应答通道优先：同步请求先消费，避免被实时流量打断
      if (this.isWaitingForResponse && this.commandResolver) {
        this.commandResolver(value)
        this.clearCommandState()
        return // 不再作为实时数据处理
      }

      // 2) 其余数据作为实时数据广播给上层
      this.emit('data-received', value)

      // 3) 实时帧也可兜底确认连接成功（防止首轮查询偶发超时）
      if (status) this.emit('status-updated', status)
      if (status && !this.isDeviceConnected) {
        const ok = this.checkDeviceConnectionStatus(status)
        if (ok) {
          console.log('[HID] 已确认设备连接成功！')
          this.isDeviceConnected = true
          const saved = loadStoredState()
          saveStoredState({ ...saved, isDeviceConnected: true })
          this.emit('connection-stage', { stage: 'connected', connectionType: this.getConnectionType() })
          this.getDeviceDetail().then(detail => {
            if (detail) this.emit('device-connected', detail)
          })
        }
      }
    }

    this.device.addEventListener('inputreport', this.inputReportHandler)
  }

  /** 只移除 inputreport 监听，不移除全局 HID 连接/断开监听。 */
  private removeInputListener(): void {
    if (this.device && this.inputReportHandler) {
      this.device.removeEventListener('inputreport', this.inputReportHandler)
      this.inputReportHandler = null
    }
    this.clearCommandState()
  }

  /** 断开当前设备但保留全局 HID 监听（用于热切换） */
  private async disconnectCurrent(): Promise<void> {
    this.removeInputListener()
    if (this.device?.opened) {
      try { await this.device.close() } catch { /* 忽略 */ }
    }
    this.device = null
    this.isDeviceConnected = false
    clearStoredState()
    this.emit('device-disconnected', undefined)
  }

  private removeEventListeners(): void {
    this.removeInputListener()

    if (navigator.hid) {
      if (this.externalDisconnectHandler) {
        navigator.hid.removeEventListener('disconnect', this.externalDisconnectHandler)
      }
      if (this.externalConnectHandler) {
        navigator.hid.removeEventListener('connect', this.externalConnectHandler)
      }
    }
  }

  private setupGlobalHIDListeners(): void {
    if (!navigator.hid) return

    // 避免重复绑定
    if (this.externalDisconnectHandler) {
      navigator.hid.removeEventListener('disconnect', this.externalDisconnectHandler)
    }
    if (this.externalConnectHandler) {
      navigator.hid.removeEventListener('connect', this.externalConnectHandler)
    }

    // 插入：记录最新插入设备 key，若当前无连接则自动连接；若已连接则切换到新设备
    this.externalConnectHandler = async (e: HIDConnectionEvent) => {
      const dev = e.device
      // Ignore secondary HID interfaces without the normal report, matching the legacy page.
      if (!this.hasNormalModeReport49(dev)) return
      this.emit('device-inserted', { connectionType: this.getConnectionTypeForDevice(dev) })
      if (this.hasNormalModeReport49(dev)) {
        this.lastConnectedDeviceKey = `${dev.vendorId}:${dev.productId}`
      }
      if (this.device) {
        // 已有连接：先断开当前，再连新设备（取最新插入优先）
        await this.disconnectCurrent()
      }
      await this.autoConnect()
    }

    // 拔出：断开当前，尝试切换到其他已授权设备
    this.externalDisconnectHandler = async (e: HIDConnectionEvent) => {
      if (!this.hasNormalModeReport49(e.device)) return
      const connectionType = this.getConnectionTypeForDevice(e.device)
      this.emit('device-removed', { connectionType })
      if (e.device !== this.device) return
      this.removeInputListener()
      this.device = null
      this.isDeviceConnected = false
      clearStoredState()
      this.emit('device-disconnected', undefined)

      // 尝试切换到剩余已授权设备
      const remaining = await this.getAuthorizedDevices()
      if (remaining.length > 0) {
        await this.autoConnect()
      } else {
        this.emit('connection-stage', { stage: 'disconnected', message: '设备已移除', connectionType })
      }
    }

    navigator.hid.addEventListener('connect', this.externalConnectHandler)
    navigator.hid.addEventListener('disconnect', this.externalDisconnectHandler)
  }
}

// 单例实例
export const hidManager = new HIDDeviceManager()
